import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button as UIButton } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  MapPin,
  Users,
  Eye,
  Clock,
  TrendingUp,
  Activity,
  Search,
  MousePointer,
  AlertTriangle,
  RefreshCw,
  Download,
  Target,
  Globe,
  Smartphone,
  BarChart3,
  Monitor,
  Apple,
} from "lucide-react";
import { LocationChart } from "@/components/LocationChart";
import { TrafficSourceChart } from "@/components/TrafficSourceChart";
import { DeviceIcon, getDeviceBreakdown } from "@/components/DeviceIcon";
import DashboardNavigation from "@/components/DashboardNavigation";
import { LandingPageAnalyticsWidget } from "@/components/widgets/LandingPageAnalyticsWidget";

interface AnalyticsStats {
  totalSessions: number;
  totalEvents: number;
  totalPageViews: number;
  uniqueVisitors: number;
  sessionsToday: number;
  sessionsThisWeek: number;
  sessionsThisMonth: number;
  popularPages: Array<{ page: string; views: number }>;
  deviceStats: {
    types: Record<string, number>;
    os: Record<string, number>;
    browsers: Record<string, number>;
  };
  locationStats: Array<{ location: string; count: number }>;
  trafficSources: {
    utm: Record<string, number>;
    searchEngines: Record<string, number>;
    referrers: Record<string, number>;
  };
  locationsBySource: Record<string, Record<string, number>>;
  searchKeywords: Array<{ keyword: string; count: number }>;
  deviceBreakdownBySource: Record<
    string,
    Array<{ type: string; os: string; count: number }>
  >;
  topDevicesBySource: Record<
    string,
    { type: string; os: string; count: number; percentage: number }
  >;
  userJourneys: Array<{
    sessionId: string;
    pageViews: string[];
    startTime: number;
    duration: number;
    device: string;
    location: string;
  }>;
  conversions: any[];
  searches: any[];
  errors: any[];
  realTimeVisitors: number;
}

export default function EnhancedAnalyticsDashboard() {
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });
  const [dateRangeType, setDateRangeType] = useState<
    "all" | "today" | "7d" | "30d" | "custom"
  >("all");

  const getDateRangeParams = useCallback(() => {
    let start = null;
    let end = null;

    console.log("[Dashboard getDateRangeParams] dateRangeType:", dateRangeType);

    if (dateRangeType === "today") {
      end = new Date();
      start = new Date(end.getTime());
      // Set start to beginning of today
      start.setHours(0, 0, 0, 0);
      // Set end to end of today
      end.setHours(23, 59, 59, 999);
      console.log(
        "[Dashboard getDateRangeParams] Today range - start:",
        start.toISOString(),
        "end:",
        end.toISOString(),
      );
    } else if (dateRangeType === "7d") {
      end = new Date();
      start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
      console.log(
        "[Dashboard getDateRangeParams] 7d range - start:",
        start.toISOString(),
        "end:",
        end.toISOString(),
      );
    } else if (dateRangeType === "30d") {
      end = new Date();
      start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
      console.log(
        "[Dashboard getDateRangeParams] 30d range - start:",
        start.toISOString(),
        "end:",
        end.toISOString(),
      );
    } else if (dateRangeType === "custom") {
      // Only apply custom range if both dates are set
      if (dateRange.startDate && dateRange.endDate) {
        // Create dates at start of day (00:00:00) in local timezone
        start = new Date(dateRange.startDate);
        start.setHours(0, 0, 0, 0);

        // Create date at end of day (23:59:59) in local timezone
        end = new Date(dateRange.endDate);
        end.setHours(23, 59, 59, 999);
        console.log(
          "[Dashboard getDateRangeParams] Custom range - start:",
          start.toISOString(),
          "end:",
          end.toISOString(),
        );
      } else {
        // If dates aren't set, don't filter (show all time)
        start = null;
        end = null;
        console.log(
          "[Dashboard getDateRangeParams] Custom range but dates not set",
        );
      }
    }

    const params = new URLSearchParams();
    if (start) {
      params.append("startDate", start.toISOString());
    }
    if (end) {
      params.append("endDate", end.toISOString());
    }
    const result = params.toString();
    console.log("[Dashboard getDateRangeParams] Result:", result);
    return result;
  }, [dateRangeType, dateRange]);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = getDateRangeParams();
      let url = "/api/analytics";
      if (params && params.length > 0) {
        url = `/api/analytics?${params}`;
      }
      console.log(
        "[Dashboard] Loading from URL:",
        url,
        "dateRangeType:",
        dateRangeType,
        "params:",
        params,
      );
      const response = await fetch(url);
      console.log("[Dashboard] Response status:", response.status);

      if (!response.ok) {
        console.error(
          "[Dashboard] API returned error status:",
          response.status,
        );
        setStats(null);
        setLoading(false);
        return;
      }

      const data = await response.json();
      console.log("[Dashboard] Received data:", data);
      setStats(data);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (error) {
      console.error("[Dashboard] Failed to load analytics:", error);
      setStats(null);
      setLoading(false);
    }
  }, [getDateRangeParams]);

  useEffect(() => {
    loadAnalytics();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadAnalytics, 30000);
    return () => clearInterval(interval);
  }, [loadAnalytics]);

  const exportData = async () => {
    try {
      const response = await fetch("/api/analytics");
      const data = await response.json();
      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tigola-analytics-${new Date().toISOString().split("T")[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export data:", error);
    }
  };

  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get individual UTM sources for tabs
  const utmSources = Object.keys(stats?.trafficSources.utm || {});
  const totalUTMVisitors = Object.values(
    stats?.trafficSources.utm || {},
  ).reduce((a, b) => a + b, 0);

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading enhanced analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Enhanced Analytics Dashboard
              </h1>
              <p className="text-gray-600">
                Comprehensive tracking with UTM sources, Google search keywords,
                and location analytics
              </p>
              {lastUpdated && (
                <p className="text-sm text-gray-500 mt-1">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <UIButton
                onClick={loadAnalytics}
                variant="outline"
                size="sm"
                disabled={loading}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </UIButton>
              <UIButton onClick={exportData} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </UIButton>
            </div>
          </div>
        </div>

        {/* Date Range Filter */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-gray-900">
                Filter by Date Range
              </h3>
              <div className="flex flex-wrap gap-3">
                <UIButton
                  variant={dateRangeType === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setDateRangeType("all");
                  }}
                >
                  All Time
                </UIButton>
                <UIButton
                  variant={dateRangeType === "today" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setDateRangeType("today");
                  }}
                >
                  Today
                </UIButton>
                <UIButton
                  variant={dateRangeType === "7d" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setDateRangeType("7d");
                  }}
                >
                  Last 7 Days
                </UIButton>
                <UIButton
                  variant={dateRangeType === "30d" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setDateRangeType("30d");
                  }}
                >
                  Last 30 Days
                </UIButton>
                <UIButton
                  variant={dateRangeType === "custom" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setDateRangeType("custom");
                  }}
                >
                  Custom Range
                </UIButton>
              </div>

              {dateRangeType === "custom" && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-4 pt-2">
                    <div className="flex-1">
                      <label className="text-xs font-medium text-gray-600 block mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={
                          dateRange.startDate
                            ? dateRange.startDate.toISOString().split("T")[0]
                            : ""
                        }
                        onChange={(e) => {
                          if (e.target.value) {
                            // Parse date string in local timezone
                            const [year, month, day] =
                              e.target.value.split("-");
                            const newDate = new Date(
                              parseInt(year),
                              parseInt(month) - 1,
                              parseInt(day),
                            );
                            setDateRange((prev) => ({
                              ...prev,
                              startDate: newDate,
                            }));
                          } else {
                            setDateRange((prev) => ({
                              ...prev,
                              startDate: null,
                            }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs font-medium text-gray-600 block mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={
                          dateRange.endDate
                            ? dateRange.endDate.toISOString().split("T")[0]
                            : ""
                        }
                        onChange={(e) => {
                          if (e.target.value) {
                            // Parse date string in local timezone
                            const [year, month, day] =
                              e.target.value.split("-");
                            const newDate = new Date(
                              parseInt(year),
                              parseInt(month) - 1,
                              parseInt(day),
                            );
                            setDateRange((prev) => ({
                              ...prev,
                              endDate: newDate,
                            }));
                          } else {
                            setDateRange((prev) => ({
                              ...prev,
                              endDate: null,
                            }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <UIButton
                    onClick={loadAnalytics}
                    disabled={
                      !dateRange.startDate || !dateRange.endDate || loading
                    }
                    size="sm"
                  >
                    {loading ? "Applying..." : "Apply Filter"}
                  </UIButton>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Dashboard Navigation */}
        <DashboardNavigation />

        {/* Key Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Real-time</CardTitle>
              <Activity className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats?.realTimeVisitors || 0}
              </div>
              <p className="text-xs text-muted-foreground">Currently online</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Sessions
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.totalSessions || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                +{stats?.sessionsToday || 0} today
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                UTM Visitors
              </CardTitle>
              <Target className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">
                {totalUTMVisitors}
              </div>
              <p className="text-xs text-muted-foreground">Tracked sources</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Page Views</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.totalPageViews || 0}
              </div>
              <p className="text-xs text-muted-foreground">All time</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Locations</CardTitle>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.locationStats?.length || 0}
              </div>
              <p className="text-xs text-muted-foreground">Unique cities</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Analytics Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-9">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="sources">UTM Sources</TabsTrigger>
            <TabsTrigger value="locations">Locations</TabsTrigger>
            <TabsTrigger value="keywords">Keywords</TabsTrigger>
            <TabsTrigger value="individual">Individual Sources</TabsTrigger>
            <TabsTrigger value="device-breakdown">Device Analytics</TabsTrigger>
            <TabsTrigger value="users">User Journeys</TabsTrigger>
            <TabsTrigger value="devices">System Info</TabsTrigger>
            <TabsTrigger value="landing-pages">Landing Pages</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="space-y-6">
              {/* Traffic Sources Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Traffic Sources Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="font-medium">UTM Sources</span>
                      <Badge variant="secondary">
                        {totalUTMVisitors} visitors
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <span className="font-medium">Search Engines</span>
                      <Badge variant="secondary">
                        {Object.values(
                          stats?.trafficSources.searchEngines || {},
                        ).reduce((a, b) => a + b, 0)}{" "}
                        visitors
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <span className="font-medium">Direct/Referral</span>
                      <Badge variant="secondary">
                        {Object.values(
                          stats?.trafficSources.referrers || {},
                        ).reduce((a, b) => a + b, 0)}{" "}
                        visitors
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Search Engine Breakdown */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5 text-blue-600" />
                    Search Engine Sources
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(() => {
                      const searchEngines =
                        stats?.trafficSources.searchEngines || {};
                      const totalSearchEngineVisitors = Object.values(
                        searchEngines,
                      ).reduce((a, b) => a + b, 0);

                      // Map engine names to display names
                      const engineLabels: Record<string, string> = {
                        google: "Google",
                        bing: "Bing",
                        yahoo: "Yahoo",
                        duckduckgo: "DuckDuckGo",
                        baidu: "Baidu",
                        yandex: "Yandex",
                      };

                      // Engine colors for visual consistency
                      const engineColors: Record<string, string> = {
                        google: "text-blue-600 bg-blue-50",
                        bing: "text-cyan-600 bg-cyan-50",
                        yahoo: "text-purple-600 bg-purple-50",
                        duckduckgo: "text-red-600 bg-red-50",
                        baidu: "text-red-700 bg-red-50",
                        yandex: "text-red-600 bg-red-50",
                      };

                      // All search engines combined
                      return (
                        <>
                          <div className="flex justify-between items-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                All Search Engines Combined
                              </h4>
                              <p className="text-sm text-gray-600 mt-1">
                                Total organic search traffic
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="text-3xl font-bold text-green-600">
                                {totalSearchEngineVisitors}
                              </div>
                              <div className="text-sm text-gray-600">
                                visitors
                              </div>
                            </div>
                          </div>

                          {/* Individual Search Engine Breakdown */}
                          {totalSearchEngineVisitors > 0 ? (
                            <div className="space-y-2">
                              <h4 className="font-semibold text-gray-900 text-sm">
                                Individual Search Engines
                              </h4>
                              <div className="space-y-2">
                                {Object.entries(searchEngines)
                                  .sort(([, a], [, b]) => b - a)
                                  .map(([engine, count]) => {
                                    const percentage = (
                                      (count / totalSearchEngineVisitors) *
                                      100
                                    ).toFixed(1);
                                    const engineLabel =
                                      engineLabels[engine] ||
                                      engine.charAt(0).toUpperCase() +
                                        engine.slice(1);
                                    const colorClass =
                                      engineColors[engine] ||
                                      "text-gray-600 bg-gray-50";

                                    return (
                                      <div
                                        key={engine}
                                        className={`flex items-center justify-between p-3 rounded-lg border ${colorClass}`}
                                      >
                                        <div className="flex items-center gap-3 flex-1">
                                          <div className="flex-1">
                                            <p className="font-medium text-gray-900">
                                              {engineLabel}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <p className="font-bold text-lg">
                                            {count}
                                          </p>
                                          <p className="text-xs text-gray-600">
                                            {percentage}%
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          ) : (
                            <div className="text-center p-4 text-gray-500">
                              No search engine traffic data available yet
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>

              {/* Top Performing Source Categories */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-purple-600" />
                    Top Performing Source Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {(() => {
                      const utmSources = stats?.trafficSources.utm || {};

                      // Define source categories
                      const categories = {
                        "Social Media Sources": [
                          "facebook",
                          "instagram",
                          "whatsapp",
                        ],
                        "Google Sources": ["google_organic", "google_maps"],
                        "Physical Marketing Sources": [
                          "business_card",
                          "poster",
                          "car_door",
                          "window_sticker",
                          "flyer",
                        ],
                        "Website Sources": ["website"],
                        "Ads Sources": [
                          "facebook_ads",
                          "instagram_ads",
                          "tiktok_ads",
                          "whatsapp_ads",
                          "google_ads",
                          "google_maps_ads",
                        ],
                      };

                      const categoryColors = {
                        "Social Media Sources": "from-pink-500 to-rose-500",
                        "Google Sources": "from-green-500 to-emerald-500",
                        "Physical Marketing Sources":
                          "from-orange-500 to-amber-500",
                        "Website Sources": "from-blue-500 to-indigo-500",
                        "Ads Sources": "from-purple-500 to-violet-500",
                      };

                      const categoryIcons = {
                        "Social Media Sources": "👥",
                        "Google Sources": "🔍",
                        "Physical Marketing Sources": "📄",
                        "Website Sources": "🌐",
                        "Ads Sources": "🎯",
                      };

                      return Object.entries(categories).map(
                        ([categoryName, sources]) => {
                          const totalVisitors = sources.reduce(
                            (sum, source) => sum + (utmSources[source] || 0),
                            0,
                          );
                          const percentage =
                            totalUTMVisitors > 0
                              ? (
                                  (totalVisitors / totalUTMVisitors) *
                                  100
                                ).toFixed(1)
                              : "0";

                          return (
                            <div
                              key={categoryName}
                              className="text-center p-4 rounded-lg border bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-md transition-shadow"
                            >
                              <div className="text-2xl mb-2">
                                {
                                  categoryIcons[
                                    categoryName as keyof typeof categoryIcons
                                  ]
                                }
                              </div>
                              <h3 className="font-semibold text-sm text-gray-900 mb-2">
                                {categoryName}
                              </h3>
                              <div
                                className={`text-2xl font-bold bg-gradient-to-r ${categoryColors[categoryName as keyof typeof categoryColors]} bg-clip-text text-transparent mb-1`}
                              >
                                {totalVisitors}
                              </div>
                              <div className="text-xs text-gray-600">
                                {percentage}% of UTM traffic
                              </div>

                              {/* Show all sources in this category, sorted by value */}
                              <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
                                {sources
                                  .map((source) => ({
                                    source,
                                    count: utmSources[source] || 0,
                                  }))
                                  .sort((a, b) => b.count - a.count) // Sort by count (highest first)
                                  .map(({ source, count }) => {
                                    const sourceLabels: Record<string, string> =
                                      {
                                        website: "Website",
                                        facebook: "Facebook",
                                        facebook_ads: "FB Ads",
                                        instagram: "Instagram",
                                        instagram_ads: "IG Ads",
                                        tiktok_ads: "TikTok Ads",
                                        google_organic: "Google",
                                        google_ads: "Google Ads",
                                        google_maps: "Maps",
                                        google_maps_ads: "Maps Ads",
                                        whatsapp: "WhatsApp",
                                        whatsapp_ads: "WA Ads",
                                        business_card: "Business Card",
                                        poster: "Poster",
                                        car_door: "Car Door",
                                        window_sticker: "Window",
                                        flyer: "Flyer",
                                      };

                                    const label =
                                      sourceLabels[source] || source;
                                    const topDevice =
                                      stats?.topDevicesBySource?.[source];

                                    return (
                                      <div
                                        key={source}
                                        className={`flex justify-between items-center text-xs ${count === 0 ? "text-gray-400" : "text-gray-600"}`}
                                      >
                                        <div className="flex items-center gap-1 flex-1 min-w-0">
                                          <span
                                            className={`${count === 0 ? "text-gray-400" : "text-gray-600"} truncate`}
                                          >
                                            {label}
                                          </span>
                                          {topDevice && count > 0 && (
                                            <DeviceIcon
                                              deviceType={
                                                topDevice.type as
                                                  | "mobile"
                                                  | "tablet"
                                                  | "desktop"
                                              }
                                              os={topDevice.os}
                                              className="h-2.5 w-2.5 flex-shrink-0"
                                            />
                                          )}
                                        </div>
                                        <span
                                          className={`font-semibold ${count === 0 ? "text-gray-400" : "text-gray-800"} ml-1`}
                                        >
                                          {count}
                                        </span>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          );
                        },
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>

              {/* Top Performing Sections */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-primary" />
                      Top Performing Links
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(stats?.trafficSources.utm || {})
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 5)
                        .map(([source, count], index) => {
                          // Source labels mapping
                          const sourceLabels: Record<string, string> = {
                            website: "Website Direct",
                            facebook: "Facebook Organic",
                            facebook_ads: "Facebook Ads",
                            instagram: "Instagram Organic",
                            instagram_ads: "Instagram Ads",
                            tiktok_ads: "TikTok Ads",
                            google_organic: "Google Organic",
                            google_ads: "Google Ads",
                            google_maps: "Google Maps",
                            google_maps_ads: "Google Maps Ads",
                            whatsapp: "WhatsApp Organic",
                            whatsapp_ads: "WhatsApp Ads",
                            business_card: "Business Card QR",
                            poster: "Shop Poster QR",
                            car_door: "Car Door Sign QR",
                            window_sticker: "Car Window Sticker",
                            flyer: "Flyers",
                          };

                          const label =
                            sourceLabels[source] || source.replace("_", " ");
                          const percentage =
                            totalUTMVisitors > 0
                              ? ((count / totalUTMVisitors) * 100).toFixed(1)
                              : "0";
                          const topDevice = stats?.topDevicesBySource?.[source];

                          return (
                            <div
                              key={source}
                              className="flex items-center justify-between"
                            >
                              <div className="flex items-center gap-3">
                                <span className="flex items-center justify-center w-6 h-6 bg-primary text-primary-foreground rounded-full text-xs font-bold">
                                  {index + 1}
                                </span>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{label}</span>
                                    {topDevice && (
                                      <DeviceIcon
                                        deviceType={
                                          topDevice.type as
                                            | "mobile"
                                            | "tablet"
                                            | "desktop"
                                        }
                                        os={topDevice.os}
                                        className="h-3 w-3"
                                      />
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {percentage}% of UTM traffic
                                    {topDevice && (
                                      <span className="ml-2">
                                        • {topDevice.percentage.toFixed(0)}%{" "}
                                        {topDevice.os}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <Badge className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                                {count}
                              </Badge>
                            </div>
                          );
                        }) || (
                        <p className="text-gray-500">
                          No UTM link data available
                        </p>
                      )}

                      {totalUTMVisitors > 0 && (
                        <div className="pt-3 border-t">
                          <div className="text-sm text-gray-600 text-center">
                            Total UTM Visitors:{" "}
                            <span className="font-semibold">
                              {totalUTMVisitors}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-green-600" />
                      Top Performing Locations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {stats?.locationStats
                        ?.slice(0, 5)
                        .map((location, index) => {
                          const totalLocationVisitors =
                            stats?.locationStats?.reduce(
                              (sum, loc) => sum + loc.count,
                              0,
                            ) || 1;
                          const percentage = (
                            (location.count / totalLocationVisitors) *
                            100
                          ).toFixed(1);

                          return (
                            <div
                              key={location.location}
                              className="flex items-center justify-between"
                            >
                              <div className="flex items-center gap-3">
                                <span className="flex items-center justify-center w-6 h-6 bg-green-600 text-white rounded-full text-xs font-bold">
                                  {index + 1}
                                </span>
                                <div>
                                  <span className="font-medium">
                                    {location.location}
                                  </span>
                                  <div className="text-xs text-gray-500">
                                    {percentage}% of all visits
                                  </div>
                                </div>
                              </div>
                              <Badge className="bg-gradient-to-r from-green-500 to-teal-600 text-white">
                                {location.count}
                              </Badge>
                            </div>
                          );
                        }) || (
                        <p className="text-gray-500">
                          No location data available
                        </p>
                      )}

                      {stats?.locationStats &&
                        stats.locationStats.length > 0 && (
                          <div className="pt-3 border-t">
                            <div className="text-sm text-gray-600 text-center">
                              Total Locations:{" "}
                              <span className="font-semibold">
                                {stats.locationStats.length}
                              </span>
                            </div>
                          </div>
                        )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* UTM Sources Tab */}
          <TabsContent value="sources">
            <TrafficSourceChart
              utmSources={stats?.trafficSources.utm || {}}
              searchEngines={stats?.trafficSources.searchEngines || {}}
              referrers={stats?.trafficSources.referrers || {}}
              totalVisitors={stats?.totalSessions || 1}
            />
          </TabsContent>

          {/* Locations Tab */}
          <TabsContent value="locations">
            <LocationChart
              locationStats={stats?.locationStats || []}
              locationsBySource={stats?.locationsBySource}
              selectedSource={selectedSource || undefined}
            />
          </TabsContent>

          {/* Keywords Tab */}
          <TabsContent value="keywords">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  Google Search Keywords
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.searchKeywords?.length ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {stats.searchKeywords.map((item, index) => (
                      <div
                        key={item.keyword}
                        className="p-4 border rounded-lg bg-gradient-to-r from-green-50 to-blue-50"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-900">
                            #{index + 1}
                          </span>
                          <Badge variant="secondary">
                            {item.count} searches
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-700">
                          "{item.keyword}"
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      No search keyword data available yet
                    </p>
                    <p className="text-sm text-gray-400">
                      Keywords will appear when users visit from Google search
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Device Analytics Tab */}
          <TabsContent value="device-breakdown">
            <div className="space-y-6">
              {/* Device Analytics Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5" />
                    Device Analytics by Source
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    Complete breakdown showing all sources including zero values
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 font-semibold">Name</th>
                          <th className="text-center p-3 font-semibold w-16">
                            <Apple
                              className="h-4 w-4 mx-auto"
                              title="iOS/macOS"
                            />
                          </th>
                          <th className="text-center p-3 font-semibold w-16">
                            <svg
                              className="h-4 w-4 mx-auto"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              title="Android"
                            >
                              <path d="M17.523 15.3414c-.5665 0-1.0253-.4588-1.0253-1.0253s.4588-1.0253 1.0253-1.0253 1.0253.4588 1.0253 1.0253-.4588 1.0253-1.0253 1.0253zm-11.046 0c-.5665 0-1.0253-.4588-1.0253-1.0253s.4588-1.0253 1.0253-1.0253 1.0253.4588 1.0253 1.0253-.4588 1.0253-1.0253 1.0253zm11.405-6.02l1.395-2.4157c.077-.1326.0315-.3017-.1011-.3787-.1326-.077-.3017-.0315-.3787.1011L17.4042 8.8495c-.6897-.3157-1.4612-.4912-2.4042-.4912s-1.7145.1755-2.4042.4912L11.208 6.6671c-.077-.1326-.2461-.1781-.3787-.1011-.1326.077-.1781.2461-.1011.3787l1.395 2.4157C10.0618 10.2971 8.535 11.7842 8.535 13.6253v.8439h6.93v-.8439c0-1.8411-1.5268-3.3282-3.465-4.2839zM24 12c0 6.6274-5.3726 12-12 12S0 18.6274 0 12 5.3726 0 12 0s12 5.3726 12 12z" />
                            </svg>
                          </th>
                          <th className="text-center p-3 font-semibold w-16">
                            <svg
                              className="h-4 w-4 mx-auto"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              title="Windows"
                            >
                              <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-13.051-1.351" />
                            </svg>
                          </th>
                          <th className="text-center p-3 font-semibold w-16">
                            <Monitor
                              className="h-4 w-4 mx-auto"
                              title="Others"
                            />
                          </th>
                          <th className="text-center p-3 font-semibold w-20">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const sourceLabels: Record<string, string> = {
                            website: "Website Direct",
                            facebook: "Facebook",
                            facebook_ads: "Facebook Ads",
                            instagram: "Instagram",
                            instagram_ads: "Instagram Ads",
                            tiktok_ads: "TikTok Ads",
                            google_organic: "Google Organic",
                            google_ads: "Google Ads",
                            google_maps: "Google Maps",
                            google_maps_ads: "Google Maps Ads",
                            whatsapp: "WhatsApp",
                            whatsapp_ads: "WhatsApp Ads",
                            business_card: "Business Card QR",
                            poster: "Shop Poster QR",
                            car_door: "Car Door Sign QR",
                            window_sticker: "Car Window Sticker",
                            flyer: "Flyers",
                          };

                          // Get all possible sources from UTM data
                          const allSources = Object.keys(
                            stats?.trafficSources.utm || {},
                          );

                          return allSources
                            .sort((a, b) => {
                              const totalA = stats?.trafficSources.utm[a] || 0;
                              const totalB = stats?.trafficSources.utm[b] || 0;
                              return totalB - totalA; // Sort by total visitors (highest first)
                            })
                            .map((source) => {
                              const label =
                                sourceLabels[source] ||
                                source.replace("_", " ");
                              const devices =
                                stats?.deviceBreakdownBySource?.[source] || [];
                              const totalVisitors =
                                stats?.trafficSources.utm[source] || 0;

                              // Count devices by type
                              const appleCount = devices
                                .filter(
                                  (d) =>
                                    d.os.toLowerCase().includes("ios") ||
                                    d.os.toLowerCase().includes("mac") ||
                                    d.os.toLowerCase().includes("iphone") ||
                                    d.os.toLowerCase().includes("ipad"),
                                )
                                .reduce((sum, d) => sum + d.count, 0);

                              const androidCount = devices
                                .filter((d) =>
                                  d.os.toLowerCase().includes("android"),
                                )
                                .reduce((sum, d) => sum + d.count, 0);

                              const windowsCount = devices
                                .filter(
                                  (d) =>
                                    d.os.toLowerCase().includes("windows") ||
                                    d.os.toLowerCase().includes("win"),
                                )
                                .reduce((sum, d) => sum + d.count, 0);

                              const othersCount = devices
                                .filter((d) => {
                                  const osLower = d.os.toLowerCase();
                                  return (
                                    !osLower.includes("ios") &&
                                    !osLower.includes("mac") &&
                                    !osLower.includes("android") &&
                                    !osLower.includes("windows") &&
                                    !osLower.includes("win") &&
                                    !osLower.includes("iphone") &&
                                    !osLower.includes("ipad")
                                  );
                                })
                                .reduce((sum, d) => sum + d.count, 0);

                              const hasData = totalVisitors > 0;
                              const rowClass = hasData
                                ? "border-b hover:bg-gray-50"
                                : "border-b bg-gray-50 text-gray-400";

                              return (
                                <tr key={source} className={rowClass}>
                                  <td
                                    className={`p-3 font-medium ${hasData ? "text-gray-900" : "text-gray-400"}`}
                                  >
                                    {label}
                                  </td>
                                  <td
                                    className={`text-center p-3 ${hasData ? "text-gray-700" : "text-gray-300"}`}
                                  >
                                    {appleCount > 0 ? appleCount : "0"}
                                  </td>
                                  <td
                                    className={`text-center p-3 ${hasData ? "text-gray-700" : "text-gray-300"}`}
                                  >
                                    {androidCount > 0 ? androidCount : "0"}
                                  </td>
                                  <td
                                    className={`text-center p-3 ${hasData ? "text-gray-700" : "text-gray-300"}`}
                                  >
                                    {windowsCount > 0 ? windowsCount : "0"}
                                  </td>
                                  <td
                                    className={`text-center p-3 ${hasData ? "text-gray-700" : "text-gray-300"}`}
                                  >
                                    {othersCount > 0 ? othersCount : "0"}
                                  </td>
                                  <td
                                    className={`text-center p-3 font-bold ${hasData ? "text-gray-900" : "text-gray-400"}`}
                                  >
                                    <Badge
                                      variant={
                                        hasData ? "default" : "secondary"
                                      }
                                      className={
                                        hasData
                                          ? "bg-primary"
                                          : "bg-gray-200 text-gray-500"
                                      }
                                    >
                                      {totalVisitors}
                                    </Badge>
                                  </td>
                                </tr>
                              );
                            });
                        })()}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-4 text-sm text-gray-600">
                    <p>
                      • Rows with zero visitors are shown in gray and will
                      update when data is available
                    </p>
                    <p>• Data refreshes automatically every 30 seconds</p>
                  </div>
                </CardContent>
              </Card>

              {/* Detailed Device Breakdown Cards */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Detailed Device Breakdown by Source
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    Individual device details for sources with visitor data
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(stats?.deviceBreakdownBySource || {})
                      .filter(
                        ([source, devices]) =>
                          (stats?.trafficSources.utm[source] || 0) > 0 &&
                          devices.length > 0,
                      )
                      .sort(([, devicesA], [, devicesB]) => {
                        const totalA = devicesA.reduce(
                          (sum, d) => sum + d.count,
                          0,
                        );
                        const totalB = devicesB.reduce(
                          (sum, d) => sum + d.count,
                          0,
                        );
                        return totalB - totalA;
                      })
                      .map(([source, devices]) => {
                        const sourceLabels: Record<string, string> = {
                          website: "Website Direct",
                          facebook: "Facebook",
                          facebook_ads: "Facebook Ads",
                          instagram: "Instagram",
                          instagram_ads: "Instagram Ads",
                          tiktok_ads: "TikTok Ads",
                          google_organic: "Google Organic",
                          google_ads: "Google Ads",
                          google_maps: "Google Maps",
                          google_maps_ads: "Google Maps Ads",
                          whatsapp: "WhatsApp",
                          whatsapp_ads: "WhatsApp Ads",
                          business_card: "Business Card QR",
                          poster: "Shop Poster QR",
                          car_door: "Car Door Sign QR",
                          window_sticker: "Car Window Sticker",
                          flyer: "Flyers",
                        };

                        const label =
                          sourceLabels[source] || source.replace("_", " ");
                        const totalVisitors = devices.reduce(
                          (sum, device) => sum + device.count,
                          0,
                        );

                        return (
                          <Card
                            key={source}
                            className="hover:shadow-md transition-shadow"
                          >
                            <CardHeader className="pb-3">
                              <CardTitle className="text-sm font-semibold">
                                {label}
                              </CardTitle>
                              <p className="text-xs text-gray-600">
                                {totalVisitors} total visitors
                              </p>
                            </CardHeader>
                            <CardContent className="pt-0">
                              <div className="space-y-2">
                                {devices.slice(0, 4).map((device, index) => {
                                  const percentage = (
                                    (device.count / totalVisitors) *
                                    100
                                  ).toFixed(1);
                                  return (
                                    <div
                                      key={`${device.type}_${device.os}`}
                                      className="flex items-center justify-between"
                                    >
                                      <div className="flex items-center gap-2">
                                        <DeviceIcon
                                          deviceType={
                                            device.type as
                                              | "mobile"
                                              | "tablet"
                                              | "desktop"
                                          }
                                          os={device.os}
                                          className="h-4 w-4"
                                        />
                                        <span className="text-xs text-gray-700">
                                          {device.os}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-semibold">
                                          {device.count}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          ({percentage}%)
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                                {devices.length > 4 && (
                                  <p className="text-xs text-gray-500 text-center pt-1">
                                    +{devices.length - 4} more devices
                                  </p>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                  {Object.keys(stats?.deviceBreakdownBySource || {}).filter(
                    (source) => (stats?.trafficSources.utm[source] || 0) > 0,
                  ).length === 0 && (
                    <div className="text-center py-8">
                      <Smartphone className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">
                        No device data available yet
                      </p>
                      <p className="text-sm text-gray-400">
                        Device breakdown will appear when visitors arrive from
                        UTM sources
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Device by Source Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Most Popular Device by Source</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats?.topDevicesBySource || {})
                      .filter(
                        ([source]) => stats?.trafficSources.utm[source] > 0,
                      )
                      .sort(
                        ([, deviceA], [, deviceB]) =>
                          deviceB.count - deviceA.count,
                      )
                      .slice(0, 10)
                      .map(([source, device], index) => {
                        const sourceLabels: Record<string, string> = {
                          website: "Website Direct",
                          facebook: "Facebook",
                          facebook_ads: "Facebook Ads",
                          instagram: "Instagram",
                          instagram_ads: "Instagram Ads",
                          tiktok_ads: "TikTok Ads",
                          google_organic: "Google Organic",
                          google_ads: "Google Ads",
                          google_maps: "Google Maps",
                          google_maps_ads: "Google Maps Ads",
                          whatsapp: "WhatsApp",
                          whatsapp_ads: "WhatsApp Ads",
                          business_card: "Business Card QR",
                          poster: "Shop Poster QR",
                          car_door: "Car Door Sign QR",
                          window_sticker: "Car Window Sticker",
                          flyer: "Flyers",
                        };

                        const label =
                          sourceLabels[source] || source.replace("_", " ");

                        return (
                          <div
                            key={source}
                            className="flex items-center justify-between p-3 border rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <span className="flex items-center justify-center w-6 h-6 bg-primary text-primary-foreground rounded-full text-xs font-bold">
                                {index + 1}
                              </span>
                              <div className="flex items-center gap-2">
                                <DeviceIcon
                                  deviceType={
                                    device.type as
                                      | "mobile"
                                      | "tablet"
                                      | "desktop"
                                  }
                                  os={device.os}
                                  className="h-5 w-5"
                                />
                                <div>
                                  <span className="font-medium">{label}</span>
                                  <div className="text-xs text-gray-600">
                                    {device.os} • {device.percentage.toFixed(1)}
                                    % of source traffic
                                  </div>
                                </div>
                              </div>
                            </div>
                            <Badge>{device.count} users</Badge>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Individual Sources Tab */}
          <TabsContent value="individual">
            <Card>
              <CardHeader>
                <CardTitle>Individual UTM Source Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Select a source to view detailed location breakdown:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <UIButton
                      variant={selectedSource === null ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedSource(null)}
                    >
                      All Sources
                    </UIButton>
                    {utmSources.map((source) => (
                      <UIButton
                        key={source}
                        variant={
                          selectedSource === source ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setSelectedSource(source)}
                      >
                        {source.replace("_", " ")} (
                        {stats?.trafficSources.utm[source]})
                      </UIButton>
                    ))}
                  </div>
                </div>
                <LocationChart
                  locationStats={stats?.locationStats || []}
                  locationsBySource={stats?.locationsBySource}
                  selectedSource={selectedSource || undefined}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Journeys Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Recent User Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats?.userJourneys?.slice(0, 10).map((journey) => {
                    // Extract device info from journey.device string (format: "mobile", "desktop", etc.)
                    const deviceType = journey.device as
                      | "mobile"
                      | "tablet"
                      | "desktop";

                    return (
                      <div
                        key={journey.sessionId}
                        className="p-4 border rounded-lg"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              <DeviceIcon
                                deviceType={deviceType}
                                className="h-4 w-4"
                                showLabel={true}
                              />
                            </div>
                            <span className="text-sm text-gray-600">
                              {journey.location}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(journey.startTime)} •{" "}
                            {formatDuration(journey.duration)}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {journey.pageViews.map((page, idx) => (
                            <Badge
                              key={idx}
                              variant="secondary"
                              className="text-xs"
                            >
                              {page}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    );
                  }) || <p>No user journey data available</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Info Tab */}
          <TabsContent value="devices">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5" />
                    Device Types
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats?.deviceStats.types || {}).map(
                      ([type, count]) => (
                        <div
                          key={type}
                          className="flex items-center justify-between"
                        >
                          <span className="capitalize">{type}</span>
                          <Badge>{count}</Badge>
                        </div>
                      ),
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Operating Systems</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats?.deviceStats.os || {}).map(
                      ([os, count]) => (
                        <div
                          key={os}
                          className="flex items-center justify-between"
                        >
                          <span>{os}</span>
                          <Badge>{count}</Badge>
                        </div>
                      ),
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Browsers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats?.deviceStats.browsers || {}).map(
                      ([browser, count]) => (
                        <div
                          key={browser}
                          className="flex items-center justify-between"
                        >
                          <span>{browser}</span>
                          <Badge>{count}</Badge>
                        </div>
                      ),
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Landing Pages Analytics Tab */}
          <TabsContent value="landing-pages">
            <LandingPageAnalyticsWidget />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
